import { SANDBOX_PRESET_TAG as T } from '../presetTags'
import { SANDBOX_PRESET_BODY_CSS, type SandboxComponentPreset } from '../../sandboxPresetTypes'

/**
 * **Single-open accordion**: each click first collapses *all* panels (set `hidden`, `aria-expanded="false"`), then
 * re-opens the clicked header if it wasn’t already open (`was`). Uses `aria-controls` ↔ `id` wiring per section.
 */
const shell = SANDBOX_PRESET_BODY_CSS

const preset: SandboxComponentPreset = {
  id: 'accordion-one',
  category: 'feedback',
  title: 'Accordion (one open)',
  description: 'Expand one section at a time with aria-expanded.',
  tags: [T.accordion, T.a11y],
  html: `<h2>FAQ</h2>

<div class="acc">
  <!-- First panel open in HTML: no hidden; aria-expanded true -->
  <button type="button" class="acc-h" aria-expanded="true" aria-controls="p1" id="h1">Shipping</button>
  <div class="acc-p" id="p1" role="region" aria-labelledby="h1"><p>Demo: orders ship in 2–3 days.</p></div>

  <button type="button" class="acc-h" aria-expanded="false" aria-controls="p2" id="h2">Returns</button>
  <div class="acc-p" id="p2" role="region" aria-labelledby="h2" hidden><p>30-day window for this fake shop.</p></div>

  <button type="button" class="acc-h" aria-expanded="false" aria-controls="p3" id="h3">Support</button>
  <div class="acc-p" id="p3" role="region" aria-labelledby="h3" hidden><p>Email demo@example.com</p></div>
</div>`,
  css: `${shell}

h2 {
  color: var(--nogrok-preview-primary);
  margin: 0 0 1rem;
}

.acc {
  max-width: 24rem;
  border: 1px solid var(--nogrok-preview-outline);
  border-radius: 10px;
  overflow: hidden;
  background: var(--nogrok-preview-surface-container);
}

.acc-h {
  width: 100%;
  text-align: left;
  padding: 0.85rem 1rem;
  font: inherit;
  font-weight: 600;
  border: 0;
  border-bottom: 1px solid var(--nogrok-preview-surface-container-high);
  background: var(--nogrok-preview-surface-rail);
  color: var(--nogrok-preview-on-surface);
  cursor: pointer;
}

.acc-h[aria-expanded='true'] {
  background: var(--nogrok-preview-selected-tint);
  border-color: color-mix(in srgb, var(--nogrok-preview-primary) 25%, transparent);
  color: var(--nogrok-preview-accent-warm);
}

.acc-p {
  padding: 0 1rem 1rem;
  border-bottom: 1px solid var(--nogrok-preview-surface-container-high);
  font-size: 0.9rem;
}

.acc-p[hidden] {
  display: none;
}`,
  js: `document.querySelectorAll('.acc-h').forEach((btn) => {
  btn.addEventListener('click', () => {
    const was = btn.getAttribute('aria-expanded') === 'true'
    const id = btn.getAttribute('aria-controls')
    const panel = id ? document.getElementById(id) : null
    // Collapse everything first (exclusive accordion)
    document.querySelectorAll('.acc-h').forEach((b) => {
      b.setAttribute('aria-expanded', 'false')
      const pid = b.getAttribute('aria-controls')
      document.getElementById(pid || '')?.setAttribute('hidden', '')
    })
    if (!was) {
      btn.setAttribute('aria-expanded', 'true')
      panel?.removeAttribute('hidden')
    }
  })
})`,
}

export default preset
